import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { updateUser } from '../../Store/users/usersSlice';
import { avatarDisplayUrl } from '../../utils/imageUrl';
import { handleAvatarImgError } from '../../utils/defaultAvatar';
import styles from '../../styles/AdminPanel.module.scss';

const UserForm = ({ user, onCancel, onSuccess, showMessage }) => {
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'USER',
    avatar: null,
    avatarPreview: '',
  });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        password: '',
        role: user.role || 'USER',
        avatar: null,
        avatarPreview: user.avatar || '',
      });
    } else {
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'USER',
        avatar: null,
        avatarPreview: '',
      });
    }
  }, [user?.id]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    
    if (name === 'avatar' && files && files[0]) {
      const file = files[0];
      if (file.size > 5 * 1024 * 1024) {
        showMessage('error', 'Розмір зображення не повинен перевищувати 5 МБ');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          avatar: file,
          avatarPreview: reader.result
        }));
      };
      reader.readAsDataURL(file);
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    showMessage('', '');

    if (!formData.name || !formData.email) {
      showMessage('error', "Ім'я та email обов'язкові для заповнення");
      return;
    }

    try {
      const uploadData = new FormData();
      uploadData.append('name', formData.name);
      uploadData.append('email', formData.email);
      uploadData.append('role', formData.role);

      if (formData.password) {
        if (formData.password.length < 6) {
          showMessage('error', 'Пароль повинен містити мінімум 6 символів');
          return;
        }
        uploadData.append('password', formData.password);
      }

      const avatarInput = e.currentTarget.elements.namedItem('avatar');
      const avatarFile = avatarInput?.files?.[0] ?? null;

      if (formData.avatarPreview?.startsWith('data:') && !avatarFile) {
        showMessage('error', 'Файл аватара втрачено. Оберіть зображення знову.');
        return;
      }

      if (avatarFile) {
        uploadData.append('avatar', avatarFile, avatarFile.name);
      }

      await dispatch(updateUser({ id: user.id, formData: uploadData })).unwrap();
      showMessage('success', 'Користувача успішно оновлено!');
      onSuccess();
    } catch (error) {
      console.error('Помилка при оновленні користувача:', error);
      showMessage('error', error || 'Помилка при оновленні користувача');
    }
  };

  return (
    <div className={styles.formPage}>
      <div className={styles.formContainer}>
        <h2 className={styles.formTitle}>
          {user ? 'Редагування користувача' : 'Створення користувача'}
        </h2>
        
        <form onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label htmlFor="avatar">Аватар</label>
            <div className={styles.avatarSection}>
              <div className={styles.avatarPreview}>
                <img
                  src={
                    formData.avatarPreview?.startsWith('data:')
                      ? formData.avatarPreview
                      : avatarDisplayUrl(formData.avatarPreview)
                  }
                  alt="Avatar preview"
                  className={styles.avatarImage}
                  onError={handleAvatarImgError}
                />
              </div>
              <input
                type="file"
                id="avatar"
                name="avatar"
                accept="image/*"
                onChange={handleChange}
                className={styles.fileInput}
              />
              <label htmlFor="avatar" className={styles.fileLabel}>
                Обрати зображення
              </label>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="name">Ім'я *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Введіть ім'я"
              required
              className={styles.formInput}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Введіть email"
              required
              className={styles.formInput}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="password">Новий пароль (залиште порожнім, якщо не хочете змінювати)</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Введіть новий пароль"
              className={styles.formInput}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="role">Роль</label>
            <select
              id="role"
              name="role"
              value={formData.role}
              onChange={handleChange}
              className={styles.formInput}
            >
              <option value="USER">Користувач</option>
              <option value="ADMIN">Адмінистратор</option>
            </select>
          </div>

          <div className={styles.formActions}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={onCancel}
            >
              Скасувати
            </button>
            <button
              type="submit"
              className={styles.submitButton}
            >
              Зберегти
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserForm;



